import { z } from 'zod';

/**
 * Personal-tier entitlements (docs/release-1/17-V2-BUILD-PLAN.md §2.6, §7 W6-0;
 * founder decision 5 + D2).
 *
 * Core planning is free forever (capture, Today, Week, Unplanned, Later, All,
 * carry-over, Add to My Work, notes, My steps, Delegated, Calendar layers,
 * Home). The features in `PERSONAL_PLUS_FEATURES` unlock with EITHER the
 * Personal plan ($5) OR a paid seat in any team I'm an active member of (D2) —
 * one boolean, `personalPlus`, for all of them.
 *
 *   GET /api/me/entitlements → MeEntitlements   (browser JWT; never 402)
 *
 * Query key `['me','entitlements']` (`web/shared/api/entitlements.ts`).
 *
 * Gating rules every W6 route follows:
 *  - A Personal-tier write without entitlement → **402** with the envelope
 *    `UpgradeRequiredErrorSchema` (`code: 'upgrade_required'`, `feature`).
 *  - Turning something OFF / clearing it is always allowed (a lapsed plan
 *    never traps data: `remindAt: null`, ritual `null`, DELETE the plan feed).
 *  - Reads of my own data are never gated.
 *  - Upsell placement (web): at most ONE locked CTA per surface, never on
 *    day 1, never on work rows (§2.6).
 *
 * Privacy: the answer is about ME only. `reason: 'team_seat'` never names the
 * team, and nothing here is ever shown to a team.
 */

export const PERSONAL_PLUS_FEATURES = [
  /** Private reminder on a planned task (`PutPlan.remindAt`). */
  'reminders',
  /** Morning "Plan your day", evening "Wrap up", weekly review pushes. */
  'rituals',
  /** App-icon badge = remaining today. */
  'app_badge',
  /** Time blocks in Week (`PutPlan.blockStart` / `blockMinutes`). */
  'time_blocks',
  /** Private "My plan" ICS feed. */
  'plan_feed',
  /** Planned vs actual per day in My Time. */
  'planned_vs_actual',
  /** Own timesheet CSV export. */
  'timesheet_export',
  /** Project finance CSV export (docs/release-1/18 §8). */
  'finance_export',
  /** ✨ Plan my day / week, cross-team digest, weekly review, standup draft (BYOK). */
  'ai_planner',
] as const;
export const PersonalPlusFeatureSchema = z.enum(PERSONAL_PLUS_FEATURES);
export type PersonalPlusFeature = z.infer<typeof PersonalPlusFeatureSchema>;

/**
 * Why I have the Personal features: my own Personal plan, or a paid seat in a
 * team I'm an active member of. When both hold, `personal_plan` wins.
 */
export const PersonalPlusReasonSchema = z.enum(['personal_plan', 'team_seat']);
export type PersonalPlusReason = z.infer<typeof PersonalPlusReasonSchema>;

export const MeEntitlementsSchema = z
  .object({
    personalPlus: z.boolean(),
    /** null exactly when `personalPlus` is false. */
    reason: PersonalPlusReasonSchema.nullable(),
  })
  .refine((v) => v.personalPlus === (v.reason !== null), {
    message: 'reason is set exactly when personalPlus is true',
    path: ['reason'],
  });
export type MeEntitlements = z.infer<typeof MeEntitlementsSchema>;

/** Free tier: no Personal features. */
export const FREE_ENTITLEMENTS: MeEntitlements = { personalPlus: false, reason: null };

// ── Errors ──────────────────────────────────────────────────────────────────

/**
 * Error codes of the Personal tier (W6). Additive, never renamed (same rule as
 * `ErrorCodes` in `errors.ts`; the integrator may fold them in there).
 */
export const PersonalTierErrorCodes = {
  /** 402 — a Personal-tier write without entitlement. */
  upgradeRequired: 'upgrade_required',
  /** 400 — a time block whose start isn't on the plan's day (my time zone). */
  planBlockOutsideDay: 'plan_block_outside_day',
  /** 404 — public plan feed: bad token / no feed / lapsed (always the same answer). */
  planFeedNotFound: 'plan_feed_not_found',
} as const;
export type PersonalTierErrorCode =
  (typeof PersonalTierErrorCodes)[keyof typeof PersonalTierErrorCodes];

/** The 402 body (`{ error: { code, message, feature } }`, the uniform envelope). */
export const UpgradeRequiredErrorSchema = z.object({
  error: z.object({
    code: z.literal(PersonalTierErrorCodes.upgradeRequired),
    message: z.string(),
    feature: PersonalPlusFeatureSchema,
  }),
});
export type UpgradeRequiredError = z.infer<typeof UpgradeRequiredErrorSchema>;

/** The `feature` of a 402 `upgrade_required` body, or null for anything else. */
export function upgradeRequiredFeature(status: number, body: unknown): PersonalPlusFeature | null {
  if (status !== 402) return null;
  const parsed = UpgradeRequiredErrorSchema.safeParse(body);
  return parsed.success ? parsed.data.error.feature : null;
}
